import React, { useState, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useClaims } from '../../contexts/ClaimsContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { downloadClaimPDF } from '../../utils/pdfGenerator';

export default function ClaimsDashboard() {
  const { language } = useLanguage();
  const { isAdmin } = useRBAC();
  const { claims: ctxClaims, deleteClaim } = useClaims();
  const realtime = useRealtime();

  // Use Supabase-backed claims when available, normalize field names
  const claims = React.useMemo(() => {
    const source = realtime?.claims?.length > 0 ? realtime?.claims : ctxClaims;
    return source?.map(c => ({
      ...c,
      guestName: c?.guest_name || c?.guestName,
      reportedBy: c?.reported_by || c?.reportedBy,
      reportDate: c?.report_date || c?.reportDate,
    }));
  }, [realtime?.claims, ctxClaims]);

  const [searchQuery, setSearchQuery] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const filtered = claims?.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return (
      c?.guestName?.toLowerCase()?.includes(q) ||
      c?.room?.toLowerCase()?.includes(q) ||
      c?.description?.toLowerCase()?.includes(q) ||
      c?.reportedBy?.toLowerCase()?.includes(q)
    );
  });

  const handleDownload = async (claim) => {
    setPdfGenerating(claim?.id);
    try {
      await downloadClaimPDF(claim, language);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfGenerating(null);
    }
  };

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <Icon name="ShieldX" size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Access Restricted</h2>
          <p className="text-muted-foreground text-sm">Only Admin users can access the Claims dashboard.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground font-heading flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                <Icon name="FileWarning" size={20} className="text-amber-600" />
              </div>
              {language === 'fr' ? 'Réclamations / Incidents' : 'Claims / Incidents'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 ml-12">
              {language === 'fr' ? 'Gérer et télécharger les rapports de réclamation' : 'Manage and download claim reports'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-2">
            <Icon name="FileWarning" size={14} className="text-amber-500" />
            <span className="font-semibold text-amber-600">{claims?.length}</span>
            <span>{language === 'fr' ? 'rapport(s) total' : 'total report(s)'}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Icon name="FileWarning" size={20} className="text-amber-600" />
              <div>
                <div className="text-2xl font-bold text-amber-700">{claims?.length}</div>
                <div className="text-xs text-amber-600">{language === 'fr' ? 'Total des réclamations' : 'Total Claims'}</div>
              </div>
            </div>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Icon name="Camera" size={20} className="text-rose-600" />
              <div>
                <div className="text-2xl font-bold text-rose-700">{claims?.reduce((acc, c) => acc + (c?.photos?.length || 0), 0)}</div>
                <div className="text-xs text-rose-600">{language === 'fr' ? 'Photos jointes' : 'Photos Attached'}</div>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Icon name="Calendar" size={20} className="text-blue-600" />
              <div>
                <div className="text-2xl font-bold text-blue-700">
                  {claims?.filter(c => c?.reportDate === new Date()?.toISOString()?.split('T')?.[0])?.length}
                </div>
                <div className="text-xs text-blue-600">{language === 'fr' ? 'Rapports aujourd\'hui' : 'Reports Today'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="relative max-w-sm">
            <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e?.target?.value)}
              placeholder={language === 'fr' ? 'Rechercher par résident, chambre, description...' : 'Search by guest, room, description...'}
              className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
        </div>

        {/* Claims List */}
        {filtered?.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-16 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <Icon name="FileWarning" size={28} className="text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-base font-semibold text-foreground">
                {language === 'fr' ? 'Aucune réclamation trouvée' : 'No claims found'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {language === 'fr' ?'Les rapports de réclamation créés lors du départ apparaîtront ici.' :'Claim reports created during check-out will appear here.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered?.map(claim => (
              <div key={claim?.id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    {/* Left: Info */}
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                        <Icon name="FileWarning" size={18} className="text-amber-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-foreground">{claim?.guestName}</h3>
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full font-medium">
                            {language === 'fr' ? 'Chambre' : 'Room'} {claim?.room}
                          </span>
                          {claim?.building && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200 rounded-full">
                              {claim?.building}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {language === 'fr' ? 'Signalé par' : 'Reported by'}: <span className="font-medium text-foreground">{claim?.reportedBy}</span>
                          {' · '}
                          {language === 'fr' ? 'Date' : 'Date'}: <span className="font-medium text-foreground">{claim?.reportDate}</span>
                        </p>
                        {claim?.department && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {language === 'fr' ? 'Département' : 'Department'}: <span className="font-medium text-foreground">{claim?.department}</span>
                          </p>
                        )}
                        <p className="text-sm text-foreground mt-2 line-clamp-2">{claim?.description}</p>

                        {/* Verification badges */}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${claim?.keyReturned ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                            <Icon name="Key" size={10} />
                            {language === 'fr' ? 'Clé' : 'Key'}: {claim?.keyReturned ? (language === 'fr' ? 'Rendue' : 'Returned') : (language === 'fr' ? 'Non rendue' : 'Not Returned')}
                          </span>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${claim?.roomCheckCompleted ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                            <Icon name="ClipboardCheck" size={10} />
                            {language === 'fr' ? 'Vérif. chambre' : 'Room Check'}: {claim?.roomCheckCompleted ? (language === 'fr' ? 'Effectuée' : 'Done') : (language === 'fr' ? 'Non effectuée' : 'Not Done')}
                          </span>
                          {claim?.photos?.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium bg-blue-100 text-blue-700 border-blue-200">
                              <Icon name="Camera" size={10} />
                              {claim?.photos?.length} {language === 'fr' ? 'photo(s)' : 'photo(s)'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setSelectedClaim(selectedClaim?.id === claim?.id ? null : claim)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-muted text-muted-foreground rounded-lg hover:bg-border transition-all"
                      >
                        <Icon name={selectedClaim?.id === claim?.id ? 'ChevronUp' : 'Eye'} size={13} />
                        {selectedClaim?.id === claim?.id ? (language === 'fr' ? 'Masquer' : 'Hide') : (language === 'fr' ? 'Détails' : 'Details')}
                      </button>
                      <button
                        onClick={() => handleDownload(claim)}
                        disabled={pdfGenerating === claim?.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all disabled:opacity-60"
                      >
                        <Icon name={pdfGenerating === claim?.id ? 'Loader' : 'Download'} size={13} className={pdfGenerating === claim?.id ? 'animate-spin' : ''} />
                        {language === 'fr' ? 'Télécharger PDF' : 'Download PDF'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded detail view */}
                  {selectedClaim?.id === claim?.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Check-in' : 'Check-in'}</p>
                          <p className="text-sm font-medium text-foreground">{claim?.checkIn || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Check-out' : 'Check-out'}</p>
                          <p className="text-sm font-medium text-foreground">{claim?.checkOut || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Statut financier' : 'Financial Status'}</p>
                          <p className="text-sm font-medium text-foreground">
                            {claim?.financialStatus === 'covered' ? (language === 'fr' ? 'Pris en charge' : 'Covered') :
                             claim?.financialStatus === 'not-covered' ? (language === 'fr' ? 'Non pris en charge' : 'Not Covered') : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{language === 'fr' ? 'ID résident' : 'Guest ID'}</p>
                          <p className="text-sm font-medium text-foreground">{claim?.guestId || '—'}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{language === 'fr' ? 'Description complète' : 'Full Description'}</p>
                        <p className="text-sm text-foreground bg-muted/40 rounded-lg p-3">{claim?.description}</p>
                      </div>

                      {claim?.photos?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{language === 'fr' ? 'Photos jointes' : 'Attached Photos'}</p>
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {claim?.photos?.map((photo, idx) => (
                              <a key={idx} href={photo} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={photo}
                                  alt={`Claim photo ${idx + 1}`}
                                  className="w-full h-16 object-cover rounded-lg border border-border hover:opacity-80 transition-opacity cursor-pointer"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
